import { useTranslations } from "next-intl"
import { SafeActionResult } from "next-safe-action"
import type { UseFormReturn } from "react-hook-form"
import { toast } from "sonner"

const SERVER_FIELD_ERRORS: Record<string, string> = {
	"@q:username_already_exists": "username",
	"@q:email_already_exists": "email",
	"@q:phone_already_exists": "phone",
}

type ActionOptions<T extends SafeActionResult<any, any>> = {
	resetTurnstile?: () => void
	onSuccess?: (data: NonNullable<T["data"]>) => void
	form?: UseFormReturn<any>
}

export type HandleActionError = <T extends SafeActionResult<any, any>>(
	result: T,
	options?: ActionOptions<T>
) => asserts result is T & { data: NonNullable<T["data"]> }

export default function useActionError() {
	const t = useTranslations("errors")

	return <T extends SafeActionResult<any, any>>(
		result: T,
		options: ActionOptions<T> = {}
	): asserts result is T & { data: NonNullable<T["data"]> } => {
		if (options.resetTurnstile) {
			window.turnstile?.reset()
			options.resetTurnstile()
		}

		if (result.validationErrors) {
			toast.error(t("@q:invalid_form"))
			throw new Error()
		}

		if (result.serverError) {
			const field = options.form
				? SERVER_FIELD_ERRORS[result.serverError]
				: undefined
			if (field) {
				options.form.setError(field as never, {
					type: "server",
					message: result.serverError,
				})
			}
			toast.error(t(result.serverError))
			throw new Error()
		}

		options.onSuccess?.(result.data!)
	}
}
